import { stringify } from 'query-string';
import { HttpClient } from 'api/http-client';
import { PermissionSet, Team, User, UserStatus, UserType } from 'api/models';
import { QueryCombinerType } from 'components/organisms/tasks/combiner';
import { FieldFilter } from 'components/organisms/tasks/filters';
import { InitWorkerRequestType, InitWorkerApi } from 'initWorker';
import type { ProjectPermissionSet } from 'stores/permission-sets';
import { ApiFilter } from 'utils/simple-search-filter/simple-search-filter';
import type { Member, MembersResponse } from './types';

interface ProjectMember {
  user: Pick<User, 'userId'>;
  permissionSet: Pick<ProjectPermissionSet, 'permissionSetType'>;
  joinedAt?: number;
  status?: boolean;
}

export interface InvitedUser extends Omit<Member, 'joinedAt' | 'active'> {
  status?: boolean;
}

export interface ProjectMembership {
  projectId: number;
  members: ProjectMember[];
  teamList?: Team[];
}

export interface MembersV2Response {
  teams: {
    [key: number]: Team;
  };

  users: {
    [key: number]: User;
  };
}

export interface UpdateExternalMembers {
  user: {
    userId: number;
  };
  permissionSet: {
    permissionSetId: number;
  };
  projectId: number;
}

export class MembersAPIClient extends HttpClient {
  public async findAll(): Promise<MembersResponse> {
    let data: MembersResponse;

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.ProjectPageMembers,
      )) as MembersResponse;
    } catch {
      data = await this.get<MembersResponse>('/members');
    }

    return data;
  }

  public async getMembersV2(): Promise<MembersV2Response> {
    let data: MembersV2Response;

    try {
      data = (await InitWorkerApi.getData(
        InitWorkerRequestType.Members,
      )) as MembersV2Response;
    } catch {
      data = await this.get<MembersV2Response>('/members/projects');
    }

    return data;
  }

  public getAllProjectMemberships = async () =>
    this.get<ProjectMembership[]>('/projects/get-members');

  public async inviteMembers(data): Promise<InvitedUser[]> {
    return this.post('/members/bulk-action', data);
  }

  public async updateMember(data: {
    user: Pick<User, 'userId' | 'defaultCapacity' | 'capacityMinutes'>;
    permissionSet: Pick<PermissionSet, 'permissionSetId'>;
  }): Promise<User> {
    return this.put('/members', data);
  }

  public exportAsCSV = async (
    filters: FieldFilter[],
    match: QueryCombinerType,
    userType: UserType = UserType.Native,
    userStatus: UserStatus = UserStatus.Active,
  ): Promise<string> => {
    return this.get<{ downloadUrl: string }>(
      `/members/export?${stringify({ userStatus })}&${stringify({
        userType,
      })}&${ApiFilter.toQueryStringFormat(filters)}&${stringify(
        {
          match: filters.length ? match : null,
        },
        { skipNull: true },
      )}`,
    ).then((response) => response.downloadUrl);
  };

  public async updateExternalMembers(
    data: UpdateExternalMembers,
  ): Promise<any> {
    return this.put(`/projects/${data.projectId}/external-members`, {
      user: {
        userId: data.user.userId,
      },
      permissionSet: {
        permissionSetId: data.permissionSet.permissionSetId,
      },
    });
  }
}

export const MembersAPI = new MembersAPIClient();

export * from './types';
